import { Component, OnInit, OnDestroy } from '@angular/core';
import { UnionDomain } from 'src/app/services/union.domain';
import { DashboardService, DashboardStats, MonthlyStats, Activity, DashboardResponse } from 'src/app/services/dashboard.service';
import { SmsService } from 'src/app/services/sms.service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit, OnDestroy {
  // Current date for display
  currentDate = new Date();
  
  // Loading state
  isLoading = false;
  
  // Subscription management
  private subscriptions: Subscription[] = [];
  
  // Dashboard statistics
  dashboardStats: DashboardStats = {
    totalAssessments: 0,
    totalCollection: 0,
    totalWards: 0,
    activeUsers: 0,
    assessmentGrowth: 0,
    collectionGrowth: 0,
    userGrowth: 0
  };

  // Monthly statistics
  monthlyStats: MonthlyStats = {
    newAssessments: 0,
    monthlyCollection: 0,
    completedTasks: 0
  };

  // Recent activities data
  recentActivities: Activity[] = [];

  // SMS Balance data
  smsBalance: number = 0;
  smsBalanceLoading: boolean = false;
  smsBalanceError: string = '';

  // Month period selection for Monthly Overview
  selectedMonthPeriod: string = 'THIS_MONTH';
  monthPeriodOptions = [
    { value: 'THIS_MONTH', label: 'This Month' },
    { value: 'LAST_MONTH', label: 'Last Month' },
    { value: 'LAST_3_MONTHS', label: 'Last 3 Months' }
  ];

  constructor(
    public unionDomain: UnionDomain,
    private dashboardService: DashboardService,
    private smsService: SmsService,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void {
    this.loadDashboardData();
    this.loadSmsBalance();
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions to prevent memory leaks
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  // Load dashboard data from API
  loadDashboardData(monthPeriod?: string): void {
    this.isLoading = true;
    
    const period = monthPeriod || this.selectedMonthPeriod;
    
    const subscription = this.dashboardService.getDashboardStats(period).subscribe({
      next: (response: DashboardResponse) => {
        this.dashboardStats = response.dashboardStats;
        this.monthlyStats = response.monthlyStats;
        this.recentActivities = response.recentActivities;
        this.isLoading = false;
        console.log('Dashboard data loaded successfully', response);
      },
      error: (error) => {
        console.error('Error loading dashboard data:', error);
        this.toastr.error('Failed to load dashboard data', 'Error');
        this.isLoading = false;
        // Keep using default/empty values on error
      }
    });
    
    this.subscriptions.push(subscription);
  }

  // Handle month period change
  onMonthPeriodChange(period: string): void {
    this.selectedMonthPeriod = period;
    console.log('Month period changed to:', period);
    this.loadDashboardData(period);
    
    // Show loading feedback to user
    const periodLabel = this.monthPeriodOptions.find(option => option.value === period)?.label || period;
    this.toastr.info(`Loading data for ${periodLabel}...`, 'Loading');
  }

  // Load SMS balance
  loadSmsBalance(): void {
    this.smsBalanceLoading = true;
    this.smsBalanceError = '';
    
    const subscription = this.smsService.getSmsBalance().subscribe({
      next: (response) => {
        if (response.response_code === 202) {
          this.smsBalance = response.balance / 	0.35;
          this.smsBalanceLoading = false;
          console.log('SMS balance loaded successfully:', response.balance);
        } else {
          this.smsBalanceError = 'Failed to load SMS balance';
          this.smsBalanceLoading = false;
        }
      },
      error: (error) => {
        console.error('Error loading SMS balance:', error);
        this.smsBalanceError = 'Error loading SMS balance';
        this.smsBalanceLoading = false;
      }
    });
    
    this.subscriptions.push(subscription);
  }

  // Refresh dashboard data
  refreshDashboard(): void {
    console.log('Refreshing dashboard...');
    this.currentDate = new Date();
    this.loadDashboardData();
    this.loadSmsBalance();
    this.toastr.info('Refreshing dashboard data...', 'Refresh');
  }

  // Get trend class for styling
  getTrendClass(value: number): string {
    if (value > 0) return 'positive';
    if (value < 0) return 'negative';
    return 'neutral';
  }

  // Format currency
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('bn-BD', {
      style: 'currency',
      currency: 'BDT',
      minimumFractionDigits: 0
    }).format(amount);
  }

  // Get activity icon class
  getActivityIconClass(type: string): string {
    const iconClasses: { [key: string]: string } = {
      assessment: 'assessment',
      collection: 'collection',
      print: 'print',
      user: 'user',
      system: 'system'
    };
    return iconClasses[type] || 'default';
  }
}
